import { Component, computed, effect, inject, untracked, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@onrsr/ui/auth';
import { LoaderComponent as Loader } from '@onrsr/ui/widgets';
import { AppShell } from './app-shell';
import { AppAbility } from './core/auth/ability';
import { canRemainOn } from './core/auth/permission.guard';
import { ReauthenticationService } from './core/auth/reauthentication.service';
import { SessionExpired } from './core/auth/components/session-expired/session-expired';
import { SessionService } from './core/auth/session.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    Loader,
    AppShell,
    SessionExpired,
  ],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.scss',
})
export class App {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  protected readonly session = inject(SessionService);
  protected readonly reauth = inject(ReauthenticationService);
  private readonly ability = inject(AppAbility);
  private layoutNavigationSynced = false;

  // Computed loading state based on auth initialization
  isLoading = computed(() => !this.authService.isInitialized());

  // Computed authentication state
  isAuthenticated = computed(() => this.authService.isUserAuthenticated());

  // Computed logout state
  isLoggingOut = computed(() => this.authService.isUserLoggingOut());

  constructor() {
    effect(() => {
      if (this.isLoading() || this.isLoggingOut()) {
        return;
      }
      if (this.isAuthenticated()) {
        this.session.load().subscribe();
      } else {
        this.session.clear();
      }
    });

    effect(() => {
      if (this.session.version() === 0) {
        return;
      }
      untracked(() => {
        if (!canRemainOn(this.router.routerState.snapshot.root, this.ability)) {
          void this.router.navigateByUrl('/dashboard');
        }
      });
    });

    effect(() => {
      if (this.isLoading() || this.isLoggingOut() || !this.isAuthenticated() || !this.session.loaded()) {
        if (!this.isAuthenticated()) {
          this.layoutNavigationSynced = false;
        }
        return;
      }

      if (this.layoutNavigationSynced) {
        return;
      }

      this.layoutNavigationSynced = true;
      queueMicrotask(() => this.syncLayoutNavigation());
    });
  }

  /** Re-run navigation after layout mounts so sidenav receives NavigationEnd. */
  private syncLayoutNavigation(): void {
    const browserPath = window.location.pathname;
    const target = browserPath === '/' || browserPath === '' ? '/dashboard' : window.location.pathname + window.location.search + window.location.hash;

    void this.router.navigateByUrl(target, {
      replaceUrl: true,
      onSameUrlNavigation: 'reload',
    });
  }
}
